import React, { useState } from 'react';
import EnhancedFileUpload from './EnhancedFileUpload';
import { useWallet } from '../utils/walletContext';
import { generateCaseId } from '../utils/caseUtils';
import { uploadJsonToIPFS } from '../utils/ipfsClient';

const UnifiedEvidenceInterface = () => {
  const { isConnected, address } = useWallet();
  const [currentStep, setCurrentStep] = useState(1);
  const [evidenceData, setEvidenceData] = useState({
    caseId: generateCaseId(),
    title: '',
    description: '',
    category: 'legal_document',
    status: 'draft',
    created: new Date().toISOString(),
    uploader: address || '',
    documents: [],
    blockchain: {
      verified: false,
      txHash: '',
      timestamp: '',
      network: 'base'
    },
    ipfsData: {
      hash: '',
      url: '',
      encrypted: false
    }
  });

  // const [currentUpload, setCurrentUpload] = useState(null);
  const [saving, setSaving] = useState(false);

  const evidenceCategories = [
    { value: 'legal_document', label: 'Legal Document' },
    { value: 'contract', label: 'Contract' },
    { value: 'correspondence', label: 'Correspondence' },
    { value: 'financial_record', label: 'Financial Record' },
    { value: 'photo_evidence', label: 'Photo Evidence' },
    { value: 'video_evidence', label: 'Video Evidence' },
    { value: 'audio_recording', label: 'Audio Recording' },
    { value: 'witness_statement', label: 'Witness Statement' },
    { value: 'expert_report', label: 'Expert Report' },
    { value: 'other', label: 'Other' }
  ];

  const steps = [
    { id: 1, title: 'Evidence Details', description: 'Basic information about your evidence' },
    { id: 2, title: 'File Upload', description: 'Upload and secure your documents' },
    { id: 3, title: 'Verification', description: 'Review and submit to blockchain' },
    { id: 4, title: 'Complete', description: 'Evidence secured and timestamped' }
  ];

  const handleFileUploadComplete = (uploadResult) => {
    const newDocument = {
      id: Date.now().toString(),
      filename: uploadResult.metadata.name,
      ipfsHash: uploadResult.path,
      ipfsUrl: uploadResult.url,
      size: uploadResult.metadata.size,
      type: uploadResult.metadata.type,
      encrypted: uploadResult.encrypted || false,
      encryptionKey: uploadResult.encryptionKey || null,
      uploadedAt: new Date().toISOString()
    };

    setEvidenceData(prev => ({
      ...prev,
      documents: [...prev.documents, newDocument],
      ipfsData: {
        hash: uploadResult.path,
        url: uploadResult.url,
        encrypted: uploadResult.encrypted || false
      }
    }));

    // setCurrentUpload(uploadResult);
    setCurrentStep(3); // Move to verification step
  };

  const handleSaveToIPFS = async () => {
    if (!evidenceData.title || evidenceData.documents.length === 0) {
      alert('Please provide a title and upload at least one document');
      return;
    }

    setSaving(true);
    try {
      // Create complete evidence package
      const evidencePackage = {
        ...evidenceData,
        savedAt: new Date().toISOString(),
        version: '1.0',
        platform: 'unified-evidence-platform'
      };

      // Upload evidence package to IPFS
      const ipfsResult = await uploadJsonToIPFS(evidencePackage);
      
      setEvidenceData(prev => ({
        ...prev,
        ipfsData: {
          ...prev.ipfsData,
          packageHash: ipfsResult.path,
          packageUrl: ipfsResult.url
        },
        status: 'saved'
      }));

      setCurrentStep(4);
    } catch (error) {
      console.error('Error saving evidence package:', error);
      alert('Failed to save evidence package. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setEvidenceData({
      caseId: generateCaseId(),
      title: '',
      description: '',
      category: 'legal_document',
      status: 'draft',
      created: new Date().toISOString(),
      uploader: address || '',
      documents: [],
      blockchain: {
        verified: false,
        txHash: '',
        timestamp: '',
        network: 'base'
      },
      ipfsData: {
        hash: '',
        url: '',
        encrypted: false
      }
    });
    setCurrentStep(1);
    // setCurrentUpload(null);
  };

  return (
    <div className="unified-evidence-interface">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          {steps.map((step, index) => (
            <div key={step.id} className="flex items-center">
              <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                currentStep >= step.id 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-200 text-gray-600'
              }`}>
                {currentStep > step.id ? '✓' : step.id}
              </div>
              {index < steps.length - 1 && (
                <div className={`w-12 h-1 mx-2 ${
                  currentStep > step.id ? 'bg-blue-600' : 'bg-gray-200'
                }`}></div>
              )}
            </div>
          ))}
        </div>
        <div className="text-center">
          <h3 className="text-lg font-semibold">{steps[currentStep - 1]?.title}</h3>
          <p className="text-gray-600 dark:text-gray-400">{steps[currentStep - 1]?.description}</p>
        </div>
      </div>

      {/* Step Content */}
      <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-md transition-colors duration-300">
        {currentStep === 1 && (
          <div className="evidence-details">
            <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Evidence Information</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Evidence Title *
                </label>
                <input
                  type="text"
                  value={evidenceData.title}
                  onChange={(e) => setEvidenceData(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Contract Agreement with XYZ Corp"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors duration-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Category
                </label>
                <select
                  value={evidenceData.category}
                  onChange={(e) => setEvidenceData(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors duration-300"
                >
                  {evidenceCategories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={evidenceData.description}
                  onChange={(e) => setEvidenceData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Describe the evidence and its relevance..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors duration-300"
                />
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-md">
                <h4 className="font-medium text-blue-900 dark:text-blue-300 mb-2">Case ID: {evidenceData.caseId}</h4>
                <p className="text-sm text-blue-700 dark:text-blue-400">
                  This unique identifier will be used to track your evidence throughout the system.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setCurrentStep(2)}
                disabled={!evidenceData.title.trim()}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue to Upload
              </button>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="file-upload">
            <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Upload Evidence Files</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              Upload your evidence files. Files will be stored securely on IPFS with optional encryption.
            </p>
            
            <EnhancedFileUpload 
              onUploadComplete={handleFileUploadComplete}
              enableEncryption={true}
            />

            {evidenceData.documents.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium mb-3 text-gray-900 dark:text-white">Uploaded Documents</h4>
                <div className="space-y-2">
                  {evidenceData.documents.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded">
                      <div>
                        <span className="font-medium text-gray-900 dark:text-white">{doc.filename}</span>
                        <span className="text-sm text-gray-500 dark:text-gray-400 ml-2">({doc.size})</span>
                        {doc.encrypted && (
                          <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                            Encrypted
                          </span>
                        )}
                      </div>
                      <a 
                        href={doc.ipfsUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View on IPFS
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-between">
              <button
                onClick={() => setCurrentStep(1)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-300"
              >
                Back
              </button>
              <button
                onClick={() => setCurrentStep(3)}
                disabled={evidenceData.documents.length === 0}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Continue to Verification
              </button>
            </div>
          </div>
        )}

        {currentStep === 3 && (
          <div className="verification">
            <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">Review & Submit</h3>
            
            <div className="space-y-4 mb-6">
              <div className="border border-gray-200 dark:border-gray-600 rounded p-4">
                <h4 className="font-medium mb-2 text-gray-900 dark:text-white">Evidence Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Title:</span>
                    <span className="ml-2 font-medium text-gray-900 dark:text-white">{evidenceData.title}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Category:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{evidenceCategories.find(c => c.value === evidenceData.category)?.label}</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Documents:</span>
                    <span className="ml-2 text-gray-900 dark:text-white">{evidenceData.documents.length} file(s)</span>
                  </div>
                  <div>
                    <span className="text-gray-600 dark:text-gray-400">Case ID:</span>
                    <span className="ml-2 font-mono text-gray-900 dark:text-white">{evidenceData.caseId}</span>
                  </div>
                </div>
              </div>

              {isConnected && (
                <div className="bg-green-50 border border-green-200 rounded p-4">
                  <h5 className="font-medium text-green-900 mb-1">Wallet Connected</h5>
                  <p className="text-sm text-green-700">
                    Address: {address}
                  </p>
                  <p className="text-sm text-green-700 mt-1">
                    Your evidence will be timestamped and recorded on the Base blockchain.
                  </p>
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-between">
              <button
                onClick={() => setCurrentStep(2)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-300"
              >
                Back
              </button>
              <button
                onClick={handleSaveToIPFS}
                disabled={saving}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                {saving ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                  </>
                ) : (
                  'Save & Secure Evidence'
                )}
              </button>
            </div>
          </div>
        )}

        {currentStep === 4 && (
          <div className="completion text-center">
            <div className="mb-6">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Evidence Successfully Secured!</h3>
              <p className="text-gray-600 dark:text-gray-400">Your evidence has been uploaded to IPFS and is ready for blockchain timestamping.</p>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6 mb-6">
              <h4 className="font-medium mb-3 text-gray-900 dark:text-white">Evidence Package Details</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Case ID:</span>
                  <span className="font-mono text-gray-900 dark:text-white">{evidenceData.caseId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">IPFS Hash:</span>
                  <a 
                    href={evidenceData.ipfsData.packageUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 font-mono text-xs"
                  >
                    {evidenceData.ipfsData.packageHash}
                  </a>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600 dark:text-gray-400">Status:</span>
                  <span className="text-green-600 font-medium">Secured on IPFS</span>
                </div>
              </div>
            </div>

            <div className="flex justify-center space-x-4">
              <button
                onClick={handleReset}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Submit New Evidence
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedEvidenceInterface;